import React, { useEffect, useState } from 'react';
import { AreaChart, Area, XAxis, YAxis, CartesianGrid, ResponsiveContainer, Tooltip } from 'recharts';
import { SupportApiService } from '../../Service/SupportApiService';
import { CommonService } from '../../Service/CommonService';
import { Spinner } from 'react-bootstrap';
import { colors } from '@mui/material';

interface ChartData {
  date: string;
  value: number;
  displayDate: string;
  lastValue: number;
  updatedValue: number;
}

interface Props {
  customerServiceRequestId: string;
}

const SellRequestPricingInsightChart: React.FC<Props> = ({ customerServiceRequestId }) => {
  const [isLoading, setIsLoading] = useState(false);
  const [chartData, setChartData] = useState<ChartData[]>([]);

  const fetchSellRequestPricingHistory = async () => {
    setIsLoading(true);
    try {
        const response = await SupportApiService.fetchSellRequestPricingHistory(customerServiceRequestId);
        const data = response?.data || [];

        const formattedData: ChartData[] = data.map((entry: any, index: number) => {
        const rawDate = entry.createdDate;
        const formattedDate = rawDate.split('T')[0];
        const displayDate = index === 0 || index === data.length - 1 || index % 4 === 0
          ? CommonService.formatDateForSupportRequest(rawDate)
          : '';

        return {
          date: formattedDate + (index % 2 === 1 ? '.5' : ''),
          value: entry.updatedValue,
          displayDate,
          lastValue: entry.lastValue,
          updatedValue: entry.updatedValue
        };
      });
      setChartData(formattedData);
    } catch (error) {
      console.log('Error occurred while API calling:', error);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchSellRequestPricingHistory();
  }, [customerServiceRequestId]);

  const formatYAxis = (value: number) => {
    if (value === 0) return '0';
    return `$${value}`;
  };

  const formatXAxis = (tickItem: string) => {
    const item = chartData.find(d => d.date === tickItem);
    return item?.displayDate || '';
  };

  const getXAxisTicks = () => {
    return chartData.filter(d => d.displayDate !== '').map(d => d.date);
  };

  const CustomTooltip = ({ active, payload }: any) => {
    if (active && payload && payload.length) {
      const data = payload[0].payload;
      const formattedDate = new Date(data.date.split('.')[0]).toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
        year: 'numeric'
      });

      return (
        <div className="bg-white border rounded shadow-sm p-3" style={{ minWidth: '200px' }}>
          <p className="mb-2 fw-semibold text-dark" style={{ fontSize: '14px' }}>
            {formattedDate}
          </p>
          <div className="d-flex align-items-center mb-2">
            <div className="rounded-circle me-2" style={{ width: '8px', height: '8px', backgroundColor: '#3A809E' }}></div>
            <span className="text-muted small me-2">Last Value:</span>
            <span className="fw-semibold small" style={{color: "#3A809E"}}>${data.lastValue}</span>
          </div>
          <div className="d-flex align-items-center">
            <div className="rounded-circle me-2" style={{ width: '8px', height: '8px', backgroundColor: '#469473' }}></div>
            <span className="text-muted small me-2">Updated Value:</span>
            <span className="fw-semibold small"  style={{color: "#469473"}}>${data.updatedValue}</span>
          </div>
        </div>
      );
    }
    return null;
  };

  return (
  <div className="bg-white p-2" style={{ height: '279px' }}>

    <div style={{ height: 'calc(100% - 60px)' }}>
      {isLoading ? (
        <div className="text-center text-muted d-flex align-items-center justify-content-center h-100">
          <Spinner/>
        </div>
      ) : chartData.length === 0 ? (
        <div className="text-center text-muted d-flex align-items-center justify-content-center h-100">
          Not enough pricing history to display the chart
        </div>
      ) : (
        <ResponsiveContainer width="100%" height="100%">
          <AreaChart
            data={chartData}
            margin={{ top: 12, right: 5, left: 0, bottom: 0 }}
          >
            <CartesianGrid stroke="#E4E4DD" vertical={false}/>
            <XAxis
              dataKey="date"
              axisLine={false}
              tickLine={false}
              tick={{ fontSize: 12, fill: '#666' }}
              tickFormatter={formatXAxis}
              ticks={getXAxisTicks()}
            />
            <YAxis
              axisLine={false}
              tickLine={false}
              tick={{ fontSize: 12, fill: '#666' }}
              tickFormatter={formatYAxis}
              domain={[0, 'auto']}
            />
            <Tooltip content={<CustomTooltip />} />
            <Area
              type="monotone"
              dataKey="value"
              stroke="#496673"
              strokeWidth={2}
              fill="url(#colorGradient)"
              fillOpacity={0.3}
            />
            <defs>
              <linearGradient id="colorGradient" x1="0" y1="0" x2="0" y2="1">
                <stop offset="5%" stopColor="#0d6efd" stopOpacity={0.3} />
                <stop offset="95%" stopColor="#0d6efd" stopOpacity={0.05} />
              </linearGradient>
            </defs>
          </AreaChart>
        </ResponsiveContainer>
      )}
    </div>
  </div>
);
};

export default SellRequestPricingInsightChart;
